import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const TermsPage = () => {
  return (
    <div data-testid="terms-page" className="pt-20 min-h-screen bg-slate-950">
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-b from-slate-900 to-slate-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/" 
            className="inline-flex items-center text-blue-400 hover:text-blue-300 mb-8"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <h1 
            className="text-3xl sm:text-4xl font-bold text-white uppercase tracking-tight mb-4"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            Terms & Conditions
          </h1>
          <p className="text-slate-400">Last updated: February 2026</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-invert prose-slate max-w-none">
            
            <div className="mb-10">
              <h2 className="text-xl font-semibold text-white mb-4">1. Introduction</h2>
              <p className="text-slate-300 leading-relaxed">
                These Terms and Conditions ("Terms") govern your use of the Nassau Engineering 
                website (nassauengineering.ie) and any services provided by Nassau Engineering Ltd. 
                ("we," "our," or "us"). By accessing our website or using our services, you agree 
                to be bound by these Terms.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-xl font-semibold text-white mb-4">2. Company Information</h2>
              <div className="text-slate-300">
                <p><strong className="text-white">Nassau Engineering Ltd.</strong></p>
                <p>Unit 4b, Canal Bank, Hume Ave</p>
                <p>Park West Industrial Park</p>
                <p>Dublin, D12 ARX3, Ireland</p>
                <p className="mt-2">Phone: (01) 630 1410</p>
                <p>Email: info@nassauengineering.ie</p>
              </div>
            </div>

            <div className="mb-10">
              <h2 className="text-xl font-semibold text-white mb-4">3. Services</h2>
              <p className="text-slate-300 leading-relaxed mb-4">
                Nassau Engineering provides steel fabrication, engineering consultancy, and related 
                services. All services are subject to:
              </p>
              <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
                <li>Availability and scheduling constraints</li>
                <li>Acceptance of project specifications and requirements</li>
                <li>Agreement on pricing and payment terms</li>
                <li>Compliance with applicable safety and industry standards</li>
              </ul>
            </div>

            <div className="mb-10">
              <h2 className="text-xl font-semibold text-white mb-4">4. Quotations & Estimates</h2>
              <p className="text-slate-300 leading-relaxed mb-4">
                All quotations and estimates provided through our website or otherwise are:
              </p>
              <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
                <li>Valid for 30 days from the date of issue unless otherwise stated</li>
                <li>Subject to change based on final specifications and material costs</li>
                <li>Non-binding until a formal contract is signed</li>
                <li>Exclusive of VAT unless explicitly stated</li>
              </ul>
            </div>

            <div className="mb-10">
              <h2 className="text-xl font-semibold text-white mb-4">5. Payment Terms</h2>
              <p className="text-slate-300 leading-relaxed mb-4">
                Unless otherwise agreed in writing:
              </p>
              <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
                <li>A deposit may be required before work commences</li>
                <li>Payment is due within 30 days of invoice date</li>
                <li>Late payments may incur interest charges</li>
                <li>We reserve the right to suspend work for overdue accounts</li>
              </ul>
            </div>

            <div className="mb-10">
              <h2 className="text-xl font-semibold text-white mb-4">6. Intellectual Property</h2>
              <p className="text-slate-300 leading-relaxed">
                All content on this website, including text, graphics, logos, images, and software, 
                is the property of Nassau Engineering Ltd. or its licensors and is protected by 
                Irish and international copyright laws. You may not reproduce, distribute, or 
                create derivative works without our express written permission.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-xl font-semibold text-white mb-4">7. Website Use</h2>
              <p className="text-slate-300 leading-relaxed mb-4">
                When using our website, you agree not to:
              </p>
              <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
                <li>Use the website for any unlawful purpose</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Transmit any malicious code or harmful content</li>
                <li>Interfere with the proper functioning of the website</li>
                <li>Collect or harvest any information without permission</li>
              </ul>
            </div>

            <div className="mb-10">
              <h2 className="text-xl font-semibold text-white mb-4">8. Limitation of Liability</h2>
              <p className="text-slate-300 leading-relaxed mb-4">
                To the fullest extent permitted by law:
              </p>
              <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
                <li>We provide this website "as is" without warranties of any kind</li>
                <li>We are not liable for any indirect, incidental, or consequential damages</li>
                <li>Our total liability shall not exceed the amount paid for services</li>
                <li>We are not responsible for third-party content or links</li>
              </ul>
            </div>

            <div className="mb-10">
              <h2 className="text-xl font-semibold text-white mb-4">9. Indemnification</h2>
              <p className="text-slate-300 leading-relaxed">
                You agree to indemnify and hold harmless Nassau Engineering Ltd., its directors, 
                employees, and agents from any claims, damages, or expenses arising from your 
                use of our website or violation of these Terms.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-xl font-semibold text-white mb-4">10. Health & Safety</h2>
              <p className="text-slate-300 leading-relaxed">
                All work undertaken by Nassau Engineering complies with relevant health and safety 
                regulations. Clients are required to provide safe access to work sites and inform 
                us of any hazards. We reserve the right to refuse or suspend work if safety 
                conditions are not met.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-xl font-semibold text-white mb-4">11. Force Majeure</h2>
              <p className="text-slate-300 leading-relaxed">
                We shall not be liable for any delay or failure to perform our obligations due to 
                circumstances beyond our reasonable control, including but not limited to natural 
                disasters, war, strikes, supply chain disruptions, or government actions.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-xl font-semibold text-white mb-4">12. Governing Law</h2>
              <p className="text-slate-300 leading-relaxed">
                These Terms shall be governed by and construed in accordance with the laws of 
                Ireland. Any disputes arising from these Terms shall be subject to the exclusive 
                jurisdiction of the Irish courts.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-xl font-semibold text-white mb-4">13. Changes to Terms</h2>
              <p className="text-slate-300 leading-relaxed">
                We reserve the right to modify these Terms at any time. Changes will be effective 
                immediately upon posting to our website. Your continued use of the website 
                constitutes acceptance of the modified Terms.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-xl font-semibold text-white mb-4">14. Severability</h2>
              <p className="text-slate-300 leading-relaxed">
                If any provision of these Terms is found to be invalid or unenforceable, the 
                remaining provisions shall continue in full force and effect.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-xl font-semibold text-white mb-4">15. Contact Us</h2>
              <p className="text-slate-300 leading-relaxed">
                If you have any questions about these Terms, please contact us at:
              </p>
              <div className="mt-4 text-slate-300">
                <p><strong className="text-white">Nassau Engineering Ltd.</strong></p>
                <p>Unit 4b, Canal Bank, Hume Ave</p>
                <p>Park West Industrial Park</p>
                <p>Dublin, D12 ARX3, Ireland</p>
                <p className="mt-2">Phone: (01) 630 1410</p>
                <p>Email: info@nassauengineering.ie</p>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsPage;
